import type { JourneyContractWay } from '../../journey/JourneyContract.ts';
import type { VOUCHER_TYPE } from '../../types/typesProduct.ts';

// N.B. No TH search_mode in memsearch. T+H uses DP
export enum MemSearchSearchModes {
  DP = 'DP',
  HO = 'HO',
}

export type AccommodationFacilities = {
  adults_only: boolean;
  children: boolean;
  disable_guest: boolean;
  entertainment: boolean;
  free_wifi: boolean;
  health_safety: boolean;
  kitchen: boolean;
  parking: boolean;
  pets_allowed: boolean;
  pool: boolean;
  seafront: boolean;
  spa: boolean;
  general_ev_charge?: boolean;
  general_fitness?: boolean;
  general_rooftop_terrace?: boolean;
  family_children_playground?: boolean;
  family_baby_sitting?: boolean;
  family_children_clubs?: boolean;
  family_children_meal?: boolean;
  family_children_pool?: boolean;
  activities_hiking?: boolean;
  activities_fishing?: boolean;
  activities_cycling?: boolean;
  activities_board_games?: boolean;
  activities_horse_riding?: boolean;
  activities_golf?: boolean;
  activities_bicycle_rental?: boolean;
  activities_canoeing?: boolean;
  activities_diving?: boolean;
  activities_windsurfing?: boolean;
  activities_water_park?: boolean;
  activities_snorkelling?: boolean;
  activities_bowling?: boolean;
  activities_billiards?: boolean;
  activities_scuba?: boolean;
  activities_massage?: boolean;
  activities_water_sport?: boolean;
  activities_evening_entertainment?: boolean;
  activities_tennis_court?: boolean;
  activities_arcade_room?: boolean;
  activities_kayaking?: boolean;
  activities_ecotours?: boolean;
  activities_yoga?: boolean;
  activities_nightclub?: boolean;
  activities_darts?: boolean;
  activities_play_area?: boolean;
  spa_indoor_pool?: boolean;
  spa_rooftop_pool?: boolean;
  spa_hammam?: boolean;
  spa_sauna?: boolean;
  spa_steam_room?: boolean;
  spa_salt_pool?: boolean;
  spa_infinity_pool?: boolean;
  spa_water_slide?: boolean;
  food_sun_terrace?: boolean;
  food_picnic?: boolean;
  food_bar?: boolean;
  food_pool_bar?: boolean;
  food_restaurant?: boolean;
  food_bbq?: boolean;
  food_minimarket?: boolean;
};

export type MemSearchProductThemeType =
  'LOCATION_BASED' | 'PURPOSE_BASED' | 'BUDGET_BASED' | 'EXPERIENCE_BASED' | 'ECO_BASED';

// The feed owns this vocabulary: TripAdvisor replaced most of the TrustYou names and keeps
// adding to it. Single source of truth: the type is derived from the list, so a name can
// never be known to one and not the other.
export const MEM_SEARCH_PRODUCT_THEME_NAMES = [
  'airporthotel',
  'artdecostyle',
  'bayview',
  'beachhotel',
  'boutiquehotel',
  'businesshotel',
  'casinohotel',
  'charming',
  'cityhotel',
  'classic',
  'clothingoptional',
  'economichotel',
  'familyhotel',
  'glamping',
  'golfhotel',
  'greatview',
  'harborview',
  'hiddengems',
  'historichotel',
  'hostel',
  'hotspringshotel',
  'lagoonview',
  'lakehotel',
  'luxuryhotel',
  'marinaview',
  'midrange',
  'modern',
  'motel',
  'mountainview',
  'naturalhotel',
  'parkview',
  'quaint',
  'quiet',
  'quirkyhotel',
  'residentialneighborhood',
  'resorthotel',
  'riverview',
  'romantichotel',
  'trendy',
  'waterparkhotel',
  'wellnesshotel',
  'wintersportshotel',
] as const;

export type MemSearchProductThemeName = (typeof MEM_SEARCH_PRODUCT_THEME_NAMES)[number];

export type MemSearchProductTheme = {
  name: MemSearchProductThemeName;
  score: number;
  type: MemSearchProductThemeType;
};

export type MemSearchProductAccommodation = {
  address: string;
  chain_name: string;
  comvel_id: number;
  orchestra_id: number;
  facilities: AccommodationFacilities;
  gallery: string[];
  image: string;
  internal_id_hotel: number;
  is_alternative: boolean;
  latitude: number;
  longitude: number;
  name: string;
  provider_id: number;
  rating: number;
  reviews_num: number;
  rating_source?: string;
  stars: number;
  type: string;
  type_id: number;
  tour_operator_code?: string;
  tour_operator_name?: string;
  themes: MemSearchProductTheme[];
};

export type MemSearchProductDatesAlternative = {
  datefrom: string;
  dateto: string;
  price: number;
  strikethrough: number;
};

export type MemSearchProductDates = {
  acc_nights: number;
  alternative: boolean | MemSearchProductDatesAlternative[];
  checkin: string;
  checkout: string;
  datefrom: string;
  dateto: string;
  nights: number;
  soldout: boolean;
  alternative_deals?: boolean;
};

export type MemSearchProductDebugPricingRules = {
  hotel_listing_price_id: number;
  hotel_listing_price_rule_id: number;
  hotel_listing_rate_type: string;
  markup_price_rule_id: number;
  markup_rule_id: number;
};

export type MemSearchProductDebugPricingDebug = {
  opaque_rate: boolean;
  hotel_cost: number;
  expected_revenues: number;
  markup: number;
  desp_amount: number;
  lunik_revenue: number;
};

export type MemSearchProductDebug = {
  business_profile: string;
  initiator: string;
  live_search_hash: number;
  opx_sort_priority: number;
  price_source: string;
  pricing_rules?: MemSearchProductDebugPricingRules;
  pricing_debug?: MemSearchProductDebugPricingDebug;
  ranking_destination: number;
  ranking_man: number;
  ranking_debug_man: number;
  ranking_pos_absolute: number;
  ranking_pos_adjusted: number; // the new position which would be applied if enable_rtco is sent
  ranking_pos_original: number; // the absolute initial ranking position
  ranking_psq: number;
  ranking_sa: number;
  ranking_sa_type: string;
  ranking_type: string;
  rtco_enabled: boolean; // the flag which is true when rtco is applied to sort hotels, normally with enable_rtco=1
  rtco_score: number;
  rtco_variation: number;
  semantic_search_hash: number;
  trace_id: string;
  user_id_hash: number;
};

export type MemSearchDistanceUspDetail = {
  distance_m: number;
  name: string;
};

export type MemSearchDistanceUsp = {
  nearest_beach?: MemSearchDistanceUspDetail;
  nearest_skilift?: MemSearchDistanceUspDetail;
};

export type MemSearchProductGeo = {
  city_display_name: string;
  city_name: string;
  country_code: string;
  country_name: string;
  destination: string;
  destination_ap: string;
  destination_ap_name: string;
  destination_name: string;
  distance_km: number;
  geodb_id: string;
  internal_id_city: number;
  leisure: number;
  origin: string;
  origin_ap: string;
  origin_ap_name: string;
  origin_name: string;
  tag_id: string;
  tag_ids: string;
  distance_usp?: MemSearchDistanceUsp;
};

export type MemSearchProductVoucher = {
  book_until: string;
  code: string;
  description: string;
  discount_currency: string;
  discount_type: VOUCHER_TYPE;
  discount_value: number;
  tc_url: string;
  usp_title?: string;
  title: string;
};

export type MemSearchProductPromotions = {
  black_friday: boolean;
  flash_sale: boolean;
  flash_sale_expiration: string;
  london_eye: boolean;
  tour_france: boolean;
  voucher?: MemSearchProductVoucher;
};

export type MemSearchRateFeatures = {
  book_now_pay_later: boolean;
  cancellation: boolean;
  mealplan: number;
  rooms: number;
};

export type MemSearchRateFlags = {
  apex: boolean;
  fallback: boolean;
  livectx: boolean;
  loyalty: boolean;
  mobile: boolean;
  myprice: boolean;
  opx: boolean;
  pinkpeople: boolean;
  plus?: boolean;
  tmp_stateful_send_searchid: boolean;
  tsh: boolean;
  voucher?: boolean;
  voucher_extra_type: number;
};

export type MemSearchRateOffers = {
  january_sale: boolean;
  skipass: boolean;
  spa: boolean;
  tickets: boolean;
  transfer: boolean;
  resort_credit?: boolean;
  children_free?: boolean;
  welcome_gift?: boolean;
};
export type MemSearchRatePackageDiscount = {
  enabled: boolean;
  total_price_fo: number;
  total_price_ho: number;
};

export type MemSearchRatePriceTotal = {
  deposit_amount: number;
  price: number;
  price_eur: number;
  strikethrough: number;
  strikethrough_eur: number;
};

export type MemSearchRatePrice = {
  age: number;
  currency: string;
  is_perperson: boolean;
  deposit_amount: number;
  price: number;
  show_discount: boolean;
  strikethrough: number;
  taxes: number;
  taxes_total: number;
  total: MemSearchRatePriceTotal;
};

export type MemSearchRatePlusPriceTotal = {
  price?: number;
  strikethrough?: number;
};

export type MemSearchRatePricePlusTotal = {
  age?: number;
  currency?: string;
  is_perperson?: boolean;
  deposit_amount?: number;
  price?: number;
  show_discount?: boolean;
  strikethrough?: number;
  taxes?: number;
  taxes_total?: number;
  total?: MemSearchRatePlusPriceTotal;
};

export type MemSearchRatePricePlus = {
  age?: number;
  currency?: string;
  is_perperson?: boolean;
  price?: number;
  show_discount?: boolean;
  strikethrough?: number;
  taxes?: number;
  taxes_total?: number;
  total?: MemSearchRatePricePlusTotal;
};

export type MemSearchRateTep = {
  cost: number;
  standard_price: number;
  discount: number;
  enabled: boolean;
  voucher: number;
};

export type MemSearchRateContext = {
  price_context?: string;
  signature?: string;
};

export type MemSearchProductRate = {
  features: MemSearchRateFeatures;
  flags: MemSearchRateFlags;
  offers: MemSearchRateOffers;
  package_discount: MemSearchRatePackageDiscount;
  price: MemSearchRatePrice;
  price_plus?: MemSearchRatePricePlus;
  tep: MemSearchRateTep;
  context?: MemSearchRateContext;
};

export type MemSearchProductTransport = {
  carrier: string;
  carrier_name: string;
  direct: boolean;
  next_day: boolean;
  reduced_checkout: boolean;
  transport_id: string;
  type: string;
};

export type MemSearchProduct = {
  accomodation: MemSearchProductAccommodation;
  dates: MemSearchProductDates;
  debug: MemSearchProductDebug;
  geo: MemSearchProductGeo;
  promotions: MemSearchProductPromotions;
  rate: MemSearchProductRate;
  transport: MemSearchProductTransport;
};

export type MemSearchCountersProductsPriceStats = {
  absolute_cheapest_price: number;
  cheapest_price_first_page: number;
  first_price_by_sorting: number;
};

export type MemSearchCounters = {
  products_count: number;
  products_count_alternative: number;
  products_count_booknowpaylater: number;
  products_count_plus?: number;
  products_count_to: number;
  products_package_discount_available: number;
  products_package_discount_enabled: number;
  products_price_stats: MemSearchCountersProductsPriceStats;
  products_results_before_mapfilter_cnt: number;
};

export type MemSearchDataAccData = {
  acc_nights: number;
  checkin: string;
  checkout: string;
};

export type MemSearchData = {
  acc_data?: MemSearchDataAccData;
  booking_window: number;
  products: MemSearchProduct[];
};

export type MemSearchFlags = {
  voucher_extra: boolean;
  opx_flexible_dates: boolean;
  apex: boolean; // the search was answered by apex rather than falling back to Vacation Catalog
};

export type MemSearchPoint = {
  hid: number;
  cid: number;
  lat: number;
  lng: number;
  price: number;
  flashsale: boolean;
  myprice: boolean;
  nights: number;
};

export type MemSearchProductsPriceStats = {
  absolute_cheapest_price: number;
  cheapest_price_first_page: number;
  first_price_by_sorting: number;
};

export type MemSearchAirport = {
  city_name: string;
  iata: string;
  lat: number;
  lon: number;
  name: string;
  selected: boolean;
  is_station: boolean;
};

export type MemSearchTransportCounters = {
  flights_counter: number;
  flights_counter_fr: number;
};

export type MemSearchOvernight = {
  hotel_checkin_date_shift_fwd: number;
  hotel_checkout_date_shift_bwd: number;
  is_overnight: boolean;
  is_overnight_inbound: boolean;
  is_overnight_outbound: boolean;
  nights_decrement: number;
};

export type MemSearchFootprint = {
  co2kg?: number;
  averageCo2Kg?: number;
};

export type MemSearchTransportData = {
  baggagePriceFrom?: number;
  flightCost: number;
  flightType: string;
  footprint?: MemSearchFootprint;
  inbound: JourneyContractWay;
  outbound: JourneyContractWay;
  overnight?: MemSearchOvernight;
  priceDiff?: number;
  score: number;
  transportId: string;
  tripId: string;
  includedTotalCheckedBags?: number;
};

export type MemSearchTransport = {
  airports: boolean | MemSearchAirport[];
  counters: MemSearchTransportCounters;
  usecase_enabled: boolean;
  search_id_invalid?: boolean;
  flight_options?: number;
  overnight_available?: boolean;
  data?: MemSearchTransportData[];
};

export type MemSearchAPI = {
  business_profile: string;
  ranking_business_profile: string;
  counters: MemSearchCounters;
  data: MemSearchData;
  flags: MemSearchFlags;
  live_search_hash: number;
  points: MemSearchPoint[];
  products_price_stats: MemSearchProductsPriceStats;
  response: string;
  search_id: number;
  instant_search: boolean;
  stats: string;
  status: string;
  success: boolean;
  t_instance: string;
  t_oss: number;
  semantic_search_hash?: number;
  user_id_hash?: number;
  transport?: MemSearchTransport;
  message?: string;
};
